<template>
  <v-card flat>
    <v-card-text>
      <!-- Общая оценка -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-alert
            :type="getReliabilityType(clientCheck.ml_index)"
            border="left"
            prominent
          >
            <v-row align="center">
              <v-col class="grow">
                <div class="text-h5">
                  {{ getReliabilityText(clientCheck.ml_index) }}
                </div>
                <div class="text-body-1 mt-1">
                  {{ getReliabilityDescription(clientCheck.ml_index) }}
                </div>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <!-- Черный список -->
        <v-col cols="12" md="4" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.blacklist_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['blacklist']"
                :disabled="rerunningChecks['blacklist']"
                @click="rerunCheck('blacklist')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <v-chip
                  :color="getScoreColor(clientCheck.blacklist_score)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.blacklist_score }}
                </v-chip>
                <v-icon
                  :color="getScoreColor(clientCheck.blacklist_score)"
                  size="48"
                >
                  {{ getScoreIcon(clientCheck.blacklist_score) }}
                </v-icon>
              </div>
              
              <div class="mt-3 text-body-2">
                <!-- Клиент в черном списке -->
                <div v-if="clientCheck.blacklist_data && (clientCheck.blacklist_data.client_is_blacklisted || (clientCheck.blacklist_data.records && clientCheck.blacklist_data.records.length > 0))" class="error--text font-weight-bold">
                  <v-icon small color="error">mdi-alert-octagon</v-icon>
                  {{ $t('parser_api.blacklist_client_in_list') }}
                </div>
                
                <!-- Не найден в черном списке -->
                <div v-else>
                  {{ $t('parser_api.blacklist_clean') }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Проверка ФССП -->
        <v-col cols="12" md="4" v-if="clientCheck.fssp_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.fssp_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['fssp']"
                :disabled="rerunningChecks['fssp']"
                @click="rerunCheck('fssp')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getFsspColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.fssp_score }}
                </v-chip>
                <v-icon
                  :color="getFsspColor()"
                  size="48"
                >
                  {{ getFsspIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.fssp_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.fssp_data.reason === 'Ошибка API' ? $t('parser_api.fssp_skipped_api_error') : (clientCheck.fssp_data.reason || $t('parser_api.fssp_skipped')) }}
              </div>
              
              <!-- Есть отфильтрованные записи -->
              <div v-else-if="clientCheck.fssp_data.filter_stats && clientCheck.fssp_data.filter_stats.filtered_out > 0" class="mt-3 text-body-2">
                <!-- Если есть и прошедшие записи -->
                <div v-if="clientCheck.fssp_data.result && clientCheck.fssp_data.result.length > 0" class="warning--text">
                  <v-icon small color="warning">mdi-alert</v-icon>
                  {{ $t('parser_api.records_found') }}: 
                  <strong>{{ clientCheck.fssp_data.filtered_count || clientCheck.fssp_data.result.length }}</strong>
                  <span v-if="clientCheck.fssp_data.total_debt && clientCheck.fssp_data.total_debt > 0">
                    , {{ $t('parser_api.total_debt_amount') }}: <strong>{{ formatCurrency(clientCheck.fssp_data.total_debt) }}</strong>
                  </span>
                </div>
                
                <!-- Отфильтрованные записи -->
                <div :class="clientCheck.fssp_data.result && clientCheck.fssp_data.result.length > 0 ? 'mt-1' : ''">
                  <span class="orange--text">
                    <v-icon small color="orange">mdi-filter</v-icon>
                    {{ $t('parser_api.filtered_out') }}: {{ clientCheck.fssp_data.filter_stats.filtered_out }}
                  </span>
                  <span class="text-caption grey--text ml-1">
                    ({{ $t('parser_api.check_manually') }})
                  </span>
                </div>
              </div>
              
              <!-- Записи найдены без отфильтрованных -->
              <div v-else-if="clientCheck.fssp_data.result && clientCheck.fssp_data.result.length > 0" class="mt-3 text-body-2 warning--text">
                <v-icon small color="warning">mdi-alert</v-icon>
                {{ $t('parser_api.records_found') }}: <strong>{{ clientCheck.fssp_data.result.length }}</strong>
                <span v-if="clientCheck.fssp_data.total_debt && clientCheck.fssp_data.total_debt > 0">
                  , {{ $t('parser_api.total_debt_amount') }}: <strong>{{ formatCurrency(clientCheck.fssp_data.total_debt) }}</strong>
                </span>
              </div>
              
              <!-- Записи не найдены -->
              <div v-else class="mt-3 text-body-2 success--text">
                <v-icon small color="success">mdi-check-circle</v-icon>
                {{ $t('parser_api.no_records') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Проверка водительского удостоверения -->
        <v-col cols="12" md="4" v-if="clientCheck.driver_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.driver_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['driver']"
                :disabled="rerunningChecks['driver']"
                @click="rerunCheck('driver')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getDriverColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.driver_score }}
                </v-chip>
                <v-icon
                  :color="getDriverColor()"
                  size="48"
                >
                  {{ getDriverIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.driver_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.driver_data.reason === 'Ошибка API' ? $t('parser_api.driver_skipped_api_error') : (clientCheck.driver_data.reason || $t('parser_api.driver_skipped')) }}
              </div>
              
              <!-- ВУ не найдено -->
              <div v-else-if="clientCheck.driver_data.status === 'NOT_FOUND'" class="mt-3 text-body-2 grey--text">
                {{ $t('parser_api.driver_not_found') }}
              </div>
              
              <!-- ВУ найдено -->
              <div v-else-if="clientCheck.driver_data.document" class="mt-3 text-body-2">
                <!-- Статус -->
                <span v-if="clientCheck.driver_data.status === 'Действует' && !clientCheck.driver_data.decision" class="success--text">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  {{ $t('parser_api.driver_valid') }}
                </span>
                <span v-else-if="clientCheck.driver_data.status === 'Недействителен'" class="error--text">
                  <v-icon small color="error">mdi-close-circle</v-icon>
                  {{ $t('parser_api.driver_invalid') }}
                </span>
                
                <!-- Лишение -->
                <div v-if="clientCheck.driver_data.decision" class="mt-2 error--text">
                  <v-icon small color="error">mdi-alert</v-icon>
                  {{ $t('parser_api.driver_deprivation') }}
                </div>
                
                <!-- Критичное предупреждение -->
                <div v-if="clientCheck.driver_score === 0" class="mt-2 error--text font-weight-bold">
                  {{ $t('parser_api.driver_critical') }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Проверка КБМ -->
        <v-col cols="12" md="4" v-if="clientCheck.kbm_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.kbm_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['kbm']"
                :disabled="rerunningChecks['kbm']"
                @click="rerunCheck('kbm')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getKbmColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.kbm_score }}
                </v-chip>
                <v-icon
                  :color="getKbmColor()"
                  size="48"
                >
                  {{ getKbmIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.kbm_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.kbm_data.reason === 'Ошибка API или данные недостоверны' ? $t('parser_api.kbm_skipped_api_error') : (clientCheck.kbm_data.reason || $t('parser_api.kbm_skipped')) }}
              </div>
              
              <!-- КБМ найден -->
              <div v-else-if="clientCheck.kbm_data.kbm" class="mt-3 text-body-2">
                <span :class="getKbmScoreColor(clientCheck.kbm_data.kbm) + '--text'">
                  <v-icon small :color="getKbmScoreColor(clientCheck.kbm_data.kbm)">
                    {{ getKbmScoreIcon(clientCheck.kbm_data.kbm) }}
                  </v-icon>
                  {{ $t('parser_api.kbm_value') }}: {{ clientCheck.kbm_data.kbm }}
                </span>
              </div>
              
              <!-- КБМ не найден -->
              <div v-else class="mt-3 text-body-2 grey--text">
                {{ $t('parser_api.kbm_not_found') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Проверка паспорта -->
        <v-col cols="12" md="4" v-if="clientCheck.passport_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.passport_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['passport']"
                :disabled="rerunningChecks['passport']"
                @click="rerunCheck('passport')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <v-chip
                  :color="getPassportColor(clientCheck.passport_data.status)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.passport_score }}
                </v-chip>
                <v-icon
                  :color="getPassportColor(clientCheck.passport_data.status)"
                  size="48"
                >
                  {{ getPassportIcon(clientCheck.passport_data.status) }}
                </v-icon>
              </div>
              
              <div class="mt-3 text-body-2">
                <span v-if="clientCheck.passport_data.skipped">
                  {{ clientCheck.passport_data.reason === 'Ошибка API' ? $t('parser_api.passport_skipped_api_error') : (clientCheck.passport_data.reason || $t('parser_api.passport_skipped')) }}
                </span>
                <span v-else-if="clientCheck.passport_data.status === 'VALID'" class="success--text">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  {{ $t('parser_api.passport_valid') }}
                </span>
                <span v-else-if="clientCheck.passport_data.status === 'NOT_VALID'" class="error--text">
                  <v-icon small color="error">mdi-close-circle</v-icon>
                  {{ $t('parser_api.passport_critical') }}
                </span>
                <span v-else-if="clientCheck.passport_data.status === 'NOT_FOUND'" class="error--text">
                  <v-icon small color="error">mdi-alert</v-icon>
                  {{ $t('parser_api.passport_not_found_warning') }}
                </span>
                <span v-else>
                  {{ clientCheck.passport_data.status }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Проверка ИНН -->
        <v-col cols="12" md="4" v-if="clientCheck.inn_data && Object.keys(clientCheck.inn_data).length > 0" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.inn_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['inn']"
                :disabled="rerunningChecks['inn']"
                @click="rerunCheck('inn')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getInnColor(clientCheck.inn_data.status)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.inn_score }}
                </v-chip>
                <v-icon
                  :color="getInnColor(clientCheck.inn_data.status)"
                  size="48"
                >
                  {{ getInnIcon(clientCheck.inn_data.status) }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.inn_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.inn_data.reason === 'Ошибка API' ? $t('parser_api.inn_skipped_api_error') : (clientCheck.inn_data.reason || $t('parser_api.inn_skipped')) }}
              </div>
              
              <!-- ИНН не найден -->
              <div v-else-if="clientCheck.inn_data.status === 'NOT_FOUND'" class="mt-3 text-body-2 grey--text">
                {{ $t('parser_api.inn_not_found') }}
              </div>
              
              <!-- Найденный ИНН -->
              <div v-else-if="clientCheck.inn_data.found_inn" class="mt-3 text-body-2">
                <!-- Статус совпадения -->
                <span v-if="clientCheck.inn_data.status === 'MATCH'" class="success--text">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  {{ $t('parser_api.inn_match') }}
                </span>
                <span v-else-if="clientCheck.inn_data.status === 'MISMATCH'" class="warning--text">
                  <v-icon small color="warning">mdi-alert</v-icon>
                  {{ $t('parser_api.inn_mismatch') }}
                </span>
                
                <!-- Если нет ИНН в базе - предлагаем обновить -->
                <div v-else-if="clientCheck.inn_data.status === 'FOUND_NEW'" class="mt-1">
                  <div class="mb-1">{{ $t('parser_api.inn_found_new') }}</div>
                  <v-btn x-small color="primary" @click="$emit('update-inn', clientCheck.inn_data.found_inn)">
                    {{ $t('parser_api.update_client_inn') }}
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Проверка алиментов -->
        <v-col cols="12" md="4" v-if="clientCheck.alimony_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.alimony_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['alimony']"
                :disabled="rerunningChecks['alimony']"
                @click="rerunCheck('alimony')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getAlimonyColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.alimony_score }}
                </v-chip>
                <v-icon
                  :color="getAlimonyColor()"
                  size="48"
                >
                  {{ getAlimonyIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.alimony_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.alimony_data.reason === 'Ошибка API' ? $t('parser_api.alimony_skipped_api_error') : (clientCheck.alimony_data.reason || $t('parser_api.alimony_skipped')) }}
              </div>
              
              <!-- Записи найдены -->
              <div v-else-if="clientCheck.alimony_data.records_count > 0" class="mt-3 text-body-2">
                <span class="warning--text">
                  <v-icon small color="warning">mdi-alert</v-icon>
                  {{ $t('parser_api.alimony_critical') }}
                </span>
                <div class="mt-1 grey--text">
                  {{ $t('parser_api.alimony_records_found') }}: {{ clientCheck.alimony_data.records_count }}
                </div>
              </div>
              
              <!-- Записи не найдены -->
              <div v-else class="mt-3 text-body-2 success--text">
                <v-icon small color="success">mdi-check-circle</v-icon>
                {{ $t('parser_api.no_records') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        
        <!-- Проверка ФССП розыск -->
        <v-col cols="12" md="4" v-if="clientCheck.fssp_search_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.fssp_search_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['fssp_search']"
                :disabled="rerunningChecks['fssp_search']"
                @click="rerunCheck('fssp_search')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getFsspSearchColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.fssp_search_score }}
                </v-chip>
                <v-icon
                  :color="getFsspSearchColor()"
                  size="48"
                >
                  {{ getFsspSearchIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.fssp_search_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.fssp_search_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : (clientCheck.fssp_search_data.reason || $t('parser_api.passport_skipped')) }}
              </div>
              
              <!-- Есть отфильтрованные записи -->
              <div v-else-if="clientCheck.fssp_search_data.filter_stats && clientCheck.fssp_search_data.filter_stats.filtered_out > 0" class="mt-3 text-body-2">
                <!-- Если есть и прошедшие записи -->
                <div v-if="clientCheck.fssp_search_data.result && clientCheck.fssp_search_data.result.length > 0" class="error--text">
                  <v-icon small color="error">mdi-alert</v-icon>
                  {{ $t('parser_api.wanted_found') }}: 
                  <strong>{{ clientCheck.fssp_search_data.filtered_count || clientCheck.fssp_search_data.result.length }}</strong>
                </div>
                
                <!-- Отфильтрованные записи -->
                <div :class="clientCheck.fssp_search_data.result && clientCheck.fssp_search_data.result.length > 0 ? 'mt-1' : ''">
                  <span class="orange--text">
                    <v-icon small color="orange">mdi-filter</v-icon>
                    {{ $t('parser_api.filtered_out') }}: {{ clientCheck.fssp_search_data.filter_stats.filtered_out }}
                  </span>
                  <span class="text-caption grey--text ml-1">
                    ({{ $t('parser_api.check_manually') }})
                  </span>
                </div>
              </div>
              
              <!-- Записи найдены без отфильтрованных -->
              <div v-else-if="clientCheck.fssp_search_data.result && clientCheck.fssp_search_data.result.length > 0" class="mt-3 text-body-2 error--text">
                <v-icon small color="error">mdi-alert</v-icon>
                {{ $t('parser_api.wanted_found') }}: <strong>{{ clientCheck.fssp_search_data.result.length }}</strong>
              </div>
              
              <!-- Записи не найдены -->
              <div v-else class="mt-3 text-body-2 success--text">
                <v-icon small color="success">mdi-check-circle</v-icon>
                {{ $t('parser_api.not_wanted') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Реестр залогов -->
        <v-col cols="12" md="4" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.pledge_registry') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['pledge_registry']"
                :disabled="rerunningChecks['pledge_registry']"
                @click="rerunCheck('pledge_registry')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <v-chip
                  :color="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'grey' : getScoreColor(clientCheck.pledge_score)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.pledge_score }}
                </v-chip>
                <v-icon
                  :color="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'grey' : getScoreColor(clientCheck.pledge_score)"
                  size="48"
                >
                  {{ clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.pledge_score) }}
                </v-icon>
              </div>
              
              <div class="mt-3 text-body-2">
                <!-- Skipped -->
                <span v-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.skipped">
                  {{ clientCheck.pledge_registry_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : (clientCheck.pledge_registry_data.reason || $t('parser_api.passport_skipped')) }}
                </span>
                
                <!-- Есть отфильтрованные записи -->
                <div v-else-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.filter_stats && clientCheck.pledge_registry_data.filter_stats.filtered_out > 0">
                  <!-- Если есть и прошедшие записи -->
                  <div v-if="clientCheck.pledge_registry_data.records && clientCheck.pledge_registry_data.records.length > 0">
                    {{ $t('parser_api.records_found') }}: 
                    <strong>{{ clientCheck.pledge_registry_data.filtered_count || clientCheck.pledge_registry_data.records.length }}</strong>
                  </div>
                  
                  <!-- Отфильтрованные записи -->
                  <div :class="clientCheck.pledge_registry_data.records && clientCheck.pledge_registry_data.records.length > 0 ? 'mt-1' : ''">
                    <span class="orange--text">
                      <v-icon small color="orange">mdi-filter</v-icon>
                      {{ $t('parser_api.filtered_out') }}: {{ clientCheck.pledge_registry_data.filter_stats.filtered_out }}
                    </span>
                    <span class="text-caption grey--text ml-1">
                      ({{ $t('parser_api.check_manually') }})
                    </span>
                  </div>
                </div>
                
                <!-- Записи найдены без отфильтрованных -->
                <div v-else-if="clientCheck.pledge_registry_data && clientCheck.pledge_registry_data.records && clientCheck.pledge_registry_data.records.length > 0">
                  {{ $t('parser_api.records_found') }}: 
                  <strong>{{ clientCheck.pledge_registry_data.filtered_count || clientCheck.pledge_registry_data.records.length }}</strong>
                </div>
                
                <!-- Ничего не найдено -->
                <div v-else>
                  {{ $t('parser_api.no_records') }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Арбитражные дела -->
        <v-col cols="12" md="4" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.arbitr_cases') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['arbitr_cases']"
                :disabled="rerunningChecks['arbitr_cases']"
                @click="rerunCheck('arbitr_cases')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <v-chip
                  :color="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'grey' : getScoreColor(clientCheck.arbitr_score)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.arbitr_score }}
                </v-chip>
                <v-icon
                  :color="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'grey' : getScoreColor(clientCheck.arbitr_score)"
                  size="48"
                >
                  {{ clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.arbitr_score) }}
                </v-icon>
              </div>
              
              <div class="mt-3 text-body-2">
                <!-- Skipped -->
                <span v-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.skipped">
                  {{ clientCheck.arbitr_cases_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : (clientCheck.arbitr_cases_data.reason || $t('parser_api.passport_skipped')) }}
                </span>
                
                <!-- Есть отфильтрованные записи -->
                <div v-else-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.filter_stats && clientCheck.arbitr_cases_data.filter_stats.filtered_out > 0">
                  <!-- Если есть и прошедшие записи -->
                  <div v-if="clientCheck.arbitr_cases_data.Cases && clientCheck.arbitr_cases_data.Cases.length > 0">
                    {{ $t('parser_api.records_found') }}: 
                    <strong>{{ clientCheck.arbitr_cases_data.filtered_count || clientCheck.arbitr_cases_data.Cases.length }}</strong>
                  </div>
                  
                  <!-- Отфильтрованные записи -->
                  <div :class="clientCheck.arbitr_cases_data.Cases && clientCheck.arbitr_cases_data.Cases.length > 0 ? 'mt-1' : ''">
                    <span class="orange--text">
                      <v-icon small color="orange">mdi-filter</v-icon>
                      {{ $t('parser_api.filtered_out') }}: {{ clientCheck.arbitr_cases_data.filter_stats.filtered_out }}
                    </span>
                    <span class="text-caption grey--text ml-1">
                      ({{ $t('parser_api.check_manually') }})
                    </span>
                  </div>
                </div>
                
                <!-- Записи найдены без отфильтрованных -->
                <div v-else-if="clientCheck.arbitr_cases_data && clientCheck.arbitr_cases_data.Cases && clientCheck.arbitr_cases_data.Cases.length > 0">
                  {{ $t('parser_api.records_found') }}: 
                  <strong>{{ clientCheck.arbitr_cases_data.filtered_count || clientCheck.arbitr_cases_data.Cases.length }}</strong>
                </div>
                
                <!-- Записи не найдены -->
                <span v-else>
                  {{ $t('parser_api.no_records') }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Банкротство -->
        <v-col cols="12" md="4" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.bankruptcy') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['bankruptcy']"
                :disabled="rerunningChecks['bankruptcy']"
                @click="rerunCheck('bankruptcy')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <v-chip
                  :color="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'grey' : getScoreColor(clientCheck.bankruptcy_score)"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.bankruptcy_score }}
                </v-chip>
                <v-icon
                  :color="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'grey' : getScoreColor(clientCheck.bankruptcy_score)"
                  size="48"
                >
                  {{ clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped ? 'mdi-minus-circle' : getScoreIcon(clientCheck.bankruptcy_score) }}
                </v-icon>
              </div>
              
              <div class="mt-3 text-body-2">
                <!-- Skipped -->
                <span v-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.skipped">
                  {{ clientCheck.bankruptcy_data.reason === 'Ошибка API' ? $t('parser_api.check_skipped_api_error') : (clientCheck.bankruptcy_data.reason || $t('parser_api.passport_skipped')) }}
                </span>
                
                <!-- Есть отфильтрованные записи -->
                <div v-else-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.filter_stats && clientCheck.bankruptcy_data.filter_stats.filtered_out > 0">
                  <!-- Если есть и прошедшие записи -->
                  <div v-if="clientCheck.bankruptcy_data.records && clientCheck.bankruptcy_data.records.length > 0">
                    {{ $t('parser_api.records_found') }}: 
                    <strong>{{ clientCheck.bankruptcy_data.filtered_count || clientCheck.bankruptcy_data.records.length }}</strong>
                  </div>
                  
                  <!-- Отфильтрованные записи -->
                  <div :class="clientCheck.bankruptcy_data.records && clientCheck.bankruptcy_data.records.length > 0 ? 'mt-1' : ''">
                    <span class="orange--text">
                      <v-icon small color="orange">mdi-filter</v-icon>
                      {{ $t('parser_api.filtered_out') }}: {{ clientCheck.bankruptcy_data.filter_stats.filtered_out }}
                    </span>
                    <span class="text-caption grey--text ml-1">
                      ({{ $t('parser_api.check_manually') }})
                    </span>
                  </div>
                </div>
                
                <!-- Записи найдены без отфильтрованных -->
                <div v-else-if="clientCheck.bankruptcy_data && clientCheck.bankruptcy_data.records && clientCheck.bankruptcy_data.records.length > 0">
                  {{ $t('parser_api.records_found') }}: 
                  <strong>{{ clientCheck.bankruptcy_data.filtered_count || clientCheck.bankruptcy_data.records.length }}</strong>
                </div>
                
                <!-- Записи не найдены -->
                <span v-else>
                  {{ $t('parser_api.no_records') }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Проверка штрафов водителя -->
        <v-col cols="12" md="4" v-if="clientCheck.driver_fines_data" class="d-flex">
          <v-card outlined class="flex-grow-1 d-flex flex-column">
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
              <span>{{ $t('parser_api.driver_fines_check') }}</span>
              <v-btn
                v-if="isCheckCompleted()"
                icon
                small
                :loading="rerunningChecks['driver_fines']"
                :disabled="rerunningChecks['driver_fines']"
                @click="rerunCheck('driver_fines')"
                :title="$t('parser_api.rerun_check')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <v-chip
                  :color="getDriverFinesColor()"
                  label
                  large
                >
                  {{ $t('parser_api.score') }}: {{ clientCheck.driver_fines_score }}
                </v-chip>
                <v-icon
                  :color="getDriverFinesColor()"
                  size="48"
                >
                  {{ getDriverFinesIcon() }}
                </v-icon>
              </div>
              
              <!-- Skipped -->
              <div v-if="clientCheck.driver_fines_data.skipped" class="mt-3 text-body-2">
                {{ clientCheck.driver_fines_data.reason === 'Ошибка API' ? $t('parser_api.driver_fines_skipped_api_error') : (clientCheck.driver_fines_data.reason || $t('parser_api.driver_fines_skipped')) }}
              </div>
              
              <!-- Штрафы найдены -->
              <div v-else-if="clientCheck.driver_fines_data.fines_count > 0" class="mt-3 text-body-2">
                <span class="warning--text">
                  <v-icon small color="warning">mdi-alert</v-icon>
                  {{ $t('parser_api.fines_found') }}: {{ clientCheck.driver_fines_data.fines_count }}
                </span>
                <div class="mt-1 error--text font-weight-bold">
                  {{ $t('parser_api.total_amount') }}: {{ formatCurrency(clientCheck.driver_fines_data.total_amount) }}
                </div>
              </div>
              
              <!-- Штрафы не найдены -->
              <div v-else class="mt-3 text-body-2 success--text">
                <v-icon small color="success">mdi-check-circle</v-icon>
                {{ $t('parser_api.no_fines') }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ClientCheckExpressReport',
  props: {
    clientCheck: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      rerunningChecks: {} // Отслеживание перезапускаемых проверок: { checkType: true/false }
    }
  },
  watch: {
    // Простой watch на конкретные статусы
    'clientCheck.fssp_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['fssp']) {
        this.$set(this.rerunningChecks, 'fssp', false)
      }
    },
    'clientCheck.pledge_registry_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['pledge_registry']) {
        this.$set(this.rerunningChecks, 'pledge_registry', false)
      }
    },
    'clientCheck.arbitr_cases_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['arbitr_cases']) {
        this.$set(this.rerunningChecks, 'arbitr_cases', false)
      }
    },
    'clientCheck.bankruptcy_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['bankruptcy']) {
        this.$set(this.rerunningChecks, 'bankruptcy', false)
      }
    },
    'clientCheck.passport_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['passport']) {
        this.$set(this.rerunningChecks, 'passport', false)
      }
    },
    'clientCheck.inn_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['inn']) {
        this.$set(this.rerunningChecks, 'inn', false)
      }
    },
    'clientCheck.alimony_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['alimony']) {
        this.$set(this.rerunningChecks, 'alimony', false)
      }
    },
    'clientCheck.driver_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['driver']) {
        this.$set(this.rerunningChecks, 'driver', false)
      }
    },
    'clientCheck.kbm_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['kbm']) {
        this.$set(this.rerunningChecks, 'kbm', false)
      }
    },
    'clientCheck.fssp_search_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['fssp_search']) {
        this.$set(this.rerunningChecks, 'fssp_search', false)
      }
    },
    'clientCheck.blacklist_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['blacklist']) {
        this.$set(this.rerunningChecks, 'blacklist', false)
      }
    },
    'clientCheck.driver_fines_status'(newStatus) {
      if ((newStatus === 'completed' || newStatus === 'skipped' || newStatus === 'failed') && this.rerunningChecks['driver_fines']) {
        this.$set(this.rerunningChecks, 'driver_fines', false)
      }
    }
  },
  methods: {
    getScoreColor(score) {
      if (score >= 75) return 'success'
      if (score >= 50) return 'warning'
      return 'error'
    },
    
    getScoreIcon(score) {
      if (score >= 75) return 'mdi-check-circle'
      if (score >= 50) return 'mdi-alert-circle'
      return 'mdi-close-circle'
    },
    
    getReliabilityType(mlIndex) {
      if (mlIndex >= 90) return 'success'
      if (mlIndex >= 75) return 'warning'
      return 'error'
    },
    
    getReliabilityText(mlIndex) {
      if (mlIndex >= 90) return this.$t('parser_api.reliability_high')
      if (mlIndex >= 75) return this.$t('parser_api.reliability_medium')
      return this.$t('parser_api.reliability_low')
    },
    
    getReliabilityDescription(mlIndex) {
      if (mlIndex >= 90) {
        return this.$t('parser_api.reliability_high_desc')
      }
      if (mlIndex >= 75) {
        return this.$t('parser_api.reliability_medium_desc')
      }
      return this.$t('parser_api.reliability_low_desc')
    },
    
    formatCurrency(amount) {
      if (!amount || amount === 0) return '0 ₽'
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    },
    
    getPassportColor(status) {
      if (status === 'VALID') return 'success'
      if (status === 'NOT_VALID') return 'error'
      if (status === 'NOT_FOUND') return 'error'  // Тоже критично! Паспорт не найден в базе
      return 'grey'  // SKIPPED или неизвестный статус
    },
    
    getPassportIcon(status) {
      if (status === 'VALID') return 'mdi-check-circle'
      if (status === 'NOT_VALID') return 'mdi-alert-octagon'
      if (status === 'NOT_FOUND') return 'mdi-alert-circle'  // Критично! Подозрительные данные
      return 'mdi-minus-circle'  // SKIPPED
    },
    
    getInnColor(status) {
      if (status === 'MATCH') return 'success'
      if (status === 'FOUND_NEW') return 'info'
      if (status === 'MISMATCH') return 'warning'
      if (status === 'NOT_FOUND') return 'grey'
      return 'grey'  // SKIPPED или неизвестный статус
    },
    
    getInnIcon(status) {
      if (status === 'MATCH') return 'mdi-check-circle'
      if (status === 'FOUND_NEW') return 'mdi-plus-circle'
      if (status === 'MISMATCH') return 'mdi-alert-circle'
      if (status === 'NOT_FOUND') return 'mdi-help-circle'
      return 'mdi-minus-circle'  // SKIPPED
    },
    
    getInnStatusText(status) {
      const statusMap = {
        'MATCH': this.$t('parser_api.inn_status_match'),
        'FOUND_NEW': this.$t('parser_api.inn_status_found_new'),
        'MISMATCH': this.$t('parser_api.inn_status_mismatch'),
        'NOT_FOUND': this.$t('parser_api.inn_status_not_found')
      }
      return statusMap[status] || this.$t('parser_api.inn_skipped')
    },
    
    getDriverColor() {
      if (!this.clientCheck.driver_data) return 'grey'
      if (this.clientCheck.driver_data.skipped) return 'grey'
      if (this.clientCheck.driver_data.status === 'NOT_FOUND') return 'grey'
      if (this.clientCheck.driver_score === 0) return 'error'  // Недействительно или лишение
      if (this.clientCheck.driver_data.status === 'Действует' && !this.clientCheck.driver_data.decision) return 'success'
      return 'warning'
    },
    
    getDriverIcon() {
      if (!this.clientCheck.driver_data) return 'mdi-minus-circle'
      if (this.clientCheck.driver_data.skipped) return 'mdi-minus-circle'
      if (this.clientCheck.driver_data.status === 'NOT_FOUND') return 'mdi-help-circle'
      if (this.clientCheck.driver_score === 0) return 'mdi-alert-octagon'  // Критично
      if (this.clientCheck.driver_data.status === 'Действует' && !this.clientCheck.driver_data.decision) return 'mdi-check-circle'
      return 'mdi-alert-circle'
    },
    
    getKbmColor() {
      if (!this.clientCheck.kbm_data) return 'grey'
      if (this.clientCheck.kbm_data.skipped) return 'grey'
      if (!this.clientCheck.kbm_data.kbm) return 'grey'  // Не найден
      if (this.clientCheck.kbm_score <= 30) return 'error'  // КБМ >2.0 - критично
      if (this.clientCheck.kbm_data.kbm >= 0.5 && this.clientCheck.kbm_data.kbm < 1.0) return 'success'  // Отлично
      if (this.clientCheck.kbm_data.kbm >= 1.0 && this.clientCheck.kbm_data.kbm < 1.5) return 'primary'  // Хорошо
      if (this.clientCheck.kbm_data.kbm >= 1.5 && this.clientCheck.kbm_data.kbm < 2.0) return 'warning'  // Средне
      return 'error'  // Плохо
    },
    
    getKbmIcon() {
      if (!this.clientCheck.kbm_data) return 'mdi-minus-circle'
      if (this.clientCheck.kbm_data.skipped) return 'mdi-minus-circle'
      if (!this.clientCheck.kbm_data.kbm) return 'mdi-help-circle'  // Не найден
      if (this.clientCheck.kbm_score <= 30) return 'mdi-alert-octagon'  // Критично
      if (this.clientCheck.kbm_data.kbm >= 0.5 && this.clientCheck.kbm_data.kbm < 1.0) return 'mdi-check-circle'  // Отлично
      if (this.clientCheck.kbm_data.kbm >= 1.0 && this.clientCheck.kbm_data.kbm < 1.5) return 'mdi-check'  // Хорошо
      if (this.clientCheck.kbm_data.kbm >= 1.5 && this.clientCheck.kbm_data.kbm < 2.0) return 'mdi-alert'  // Средне
      return 'mdi-close-circle'  // Плохо
    },
    
    getKbmScoreColor(kbm) {
      if (kbm >= 0.5 && kbm < 1.0) return 'success'  // Отлично
      if (kbm >= 1.0 && kbm < 1.5) return 'primary'  // Хорошо
      if (kbm >= 1.5 && kbm < 2.0) return 'warning'  // Средне
      if (kbm >= 2.0) return 'error'  // Плохо
      return 'grey'  // На всякий случай
    },
    
    getKbmScoreIcon(kbm) {
      if (kbm >= 0.5 && kbm < 1.0) return 'mdi-check-circle'  // Отлично
      if (kbm >= 1.0 && kbm < 1.5) return 'mdi-check'  // Хорошо
      if (kbm >= 1.5 && kbm < 2.0) return 'mdi-alert'  // Средне
      if (kbm >= 2.0) return 'mdi-close-circle'  // Плохо
      return 'mdi-help-circle'  // На всякий случай
    },
    
    getAlimonyColor() {
      if (!this.clientCheck.alimony_data) return 'grey'
      if (this.clientCheck.alimony_data.skipped) return 'grey'
      return this.getScoreColor(this.clientCheck.alimony_score)
    },
    
    getAlimonyIcon() {
      if (!this.clientCheck.alimony_data) return 'mdi-minus-circle'
      if (this.clientCheck.alimony_data.skipped) return 'mdi-minus-circle'
      return this.getScoreIcon(this.clientCheck.alimony_score)
    },
    
    getFsspColor() {
      if (!this.clientCheck.fssp_data) return 'grey'
      if (this.clientCheck.fssp_data.skipped) return 'grey'
      return this.getScoreColor(this.clientCheck.fssp_score)
    },
    
    getFsspIcon() {
      if (!this.clientCheck.fssp_data) return 'mdi-minus-circle'
      if (this.clientCheck.fssp_data.skipped) return 'mdi-minus-circle'
      return this.getScoreIcon(this.clientCheck.fssp_score)
    },
    
    getFsspSearchColor() {
      if (!this.clientCheck.fssp_search_data) return 'grey'
      if (this.clientCheck.fssp_search_data.skipped) return 'grey'
      return this.getScoreColor(this.clientCheck.fssp_search_score)
    },
    
    getFsspSearchIcon() {
      if (!this.clientCheck.fssp_search_data) return 'mdi-minus-circle'
      if (this.clientCheck.fssp_search_data.skipped) return 'mdi-minus-circle'
      return this.getScoreIcon(this.clientCheck.fssp_search_score)
    },
    
    getDriverFinesColor() {
      if (!this.clientCheck.driver_fines_data) return 'grey'
      if (this.clientCheck.driver_fines_data.skipped) return 'grey'
      return this.getScoreColor(this.clientCheck.driver_fines_score)
    },
    
    getDriverFinesIcon() {
      if (!this.clientCheck.driver_fines_data) return 'mdi-minus-circle'
      if (this.clientCheck.driver_fines_data.skipped) return 'mdi-minus-circle'
      return this.getScoreIcon(this.clientCheck.driver_fines_score)
    },
    
    isCheckCompleted() {
      return this.clientCheck && this.clientCheck.status === 'completed'
    },
    
    async rerunCheck(checkType) {
      // Повтор — это новый платный запрос к провайдеру: списание идёт за каждый
      // завершённый прогон. Бесплатен только локальный чёрный список.
      if (checkType !== 'blacklist') {
        const confirmed = await this.$confirm({
          title: this.$t('parser_api.rerun_paid_title'),
          text: this.$t('parser_api.rerun_paid_text'),
          icon: 'mdi-alert-circle-outline'
        })

        if (!confirmed) return
      }

      // Устанавливаем флаг загрузки для этой проверки
      this.$set(this.rerunningChecks, checkType, true)
      
      try {
        const response = await this.axios.post(
          `/api/v1/parser_api/client_checks/${this.clientCheck.client_id}/rerun/${checkType}`,
          {},
          {
            headers: { Authorization: this.$store.getters.getAuthToken }
          }
        )
        
        if (response.data.success) {
          this.$notify(
            'Успешно',
            this.$t('parser_api.rerun_check_success'),
            'success',
            3000
          )
          // Флаг загрузки будет сброшен автоматически через watch когда придет WebSocket с обновленным статусом
        }
      } catch (error) {
        console.error('Error rerunning check:', error)
        this.$notify(
          'Ошибка',
          error.response?.data?.error || this.$t('parser_api.rerun_check_error'),
          'error',
          5000
        )
        
        // При ошибке API сразу сбрасываем флаг загрузки
        this.$set(this.rerunningChecks, checkType, false)
      }
    }
  }
}
</script>

<style scoped>
</style>

